import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { Row, Col } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCommentDots, faEnvelope, faLock, faUser, faUmbrella, faReceipt,
    faRightLeft, faCar, faTriangleExclamation, faChevronRight, faChartPie,
} from '@fortawesome/free-solid-svg-icons';
import DashboardItem from './DashboardItem';
import CurrencyConverter from '../../marketdata/CurrencyConverter';
import { getRcaStatus } from '../../utils/account/rcaStatus';
import styles from '../../styles/account/Dashboard.module.scss';

const SECTIONS = [
    {
        title: 'Cereri de credit',
        info: 'Aplicațiile de credit, completate pe Conso și transmise către partenerii noștri.',
        link: '/user/credite/cereri',
        thumb: '/images/icons/personal-loans-icon.png',
        width: 60, height: 60, type: 'image',
    },
    {
        title: 'Asigurări RCA',
        info: 'Polițele RCA, achiziționate pe Conso. Ai acces oricând la polița în format electronic.',
        link: '/user/asigurari/rca',
        thumb: '/images/icons/credite-auto-icon.png',
        width: 100, height: 60, type: 'image',
    },
    {
        title: 'Buget personal',
        info: 'Venituri, cheltuieli, sold disponibil și capacitatea ta de creditare.',
        link: '/user/buget', thumb: faChartPie, type: 'icon',
    },
    {
        title: 'Oferte RCA',
        info: 'Ofertele salvate pentru asigurări RCA. Adaugă în cos și finalizează comanda.',
        link: '/user/asigurari/rca/oferte', thumb: faUmbrella, type: 'icon',
    },
    {
        title: 'Comenzi',
        info: 'Cumparaturile online efectuate pe Conso.',
        link: '/user/comenzi', thumb: faReceipt, type: 'icon',
    },
    {
        title: 'Recenzii',
        info: 'Recenziile postate la Vocea Clientului, despre experințele avute cu produsele financiare.',
        link: '/user/recenzii', thumb: faCommentDots, type: 'icon',
    },
    {
        title: 'Newsletter',
        info: 'Newsletterele la care ești abonat.',
        link: '/user/profil/newsletter', thumb: faEnvelope, type: 'icon',
    },
    {
        title: 'Date personale',
        info: 'Datele personale salvate în cont.',
        link: '/user/profil/info', thumb: faUser, type: 'icon',
    },
    {
        title: 'Schimbare Parolă',
        info: 'Modificarea parolei de acces.',
        link: '/user/profil/info', thumb: faLock, type: 'icon',
    },
];

const RcaPriorityCard = ({ status }) => {
    const expiring = status.state === 'expiring';
    const title = expiring
        ? (status.daysLeft === 0 ? 'Polița RCA expiră azi' : `Polița RCA expiră în ${status.daysLeft} ${status.daysLeft === 1 ? 'zi' : 'zile'}`)
        : 'Polița RCA a expirat';
    const text = expiring
        ? 'Reînnoiește din timp ca să circuli acoperit. Compară ofertele în câteva minute.'
        : 'Nu mai ești acoperit. Găsește o ofertă nouă și evită amenzile.';

    return (
        <div className={`${styles.priority} ${expiring ? styles.warn : styles.danger}`}>
            <div className={styles.priorityIcon}>
                <FontAwesomeIcon icon={expiring ? faCar : faTriangleExclamation} height="24" width="24" />
            </div>
            <div className={styles.priorityBody}>
                <strong>{title}</strong>
                <span>{text}</span>
            </div>
            <Link href="/asigurari-rca" className={styles.priorityCta}>
                Compară RCA
                <FontAwesomeIcon icon={faChevronRight} height="14" width="14" className="ms-2" />
            </Link>
        </div>
    );
};

const AccountDashboard = () => {
    const isLoggedIn = useSelector((state) => state.user.isLoggedIn);
    const [rcaStatus, setRcaStatus] = useState(null);

    useEffect(() => {
        let alive = true;
        if (isLoggedIn) {
            getRcaStatus().then((s) => { if (alive) setRcaStatus(s); });
        }
        return () => { alive = false; };
    }, [isLoggedIn]);

    return (
        <div className="pb-3">
            {/* Card de prioritate adaptiv: apare doar când o poliță RCA e relevantă */}
            {rcaStatus && (
                <div className="px-3 pt-3">
                    <RcaPriorityCard status={rcaStatus} />
                </div>
            )}

            {/* Cârlig de frecvență: convertorul valutar, glanceable direct pe home */}
            <div className="px-3 pt-3">
                <div className={styles.widgetCard}>
                    <div className={styles.widgetHead}>
                        <span>
                            <FontAwesomeIcon icon={faRightLeft} height="16" width="16" className="me-2" />
                            Curs valutar
                        </span>
                        <Link href="/valutar" className={styles.widgetLink}>
                            Deschide
                            <FontAwesomeIcon icon={faChevronRight} height="12" width="12" className="ms-1" />
                        </Link>
                    </div>
                    <CurrencyConverter compact />
                </div>
            </div>

            {/* Secțiunile contului */}
            <Row className="p-3 d-flex justify-content-between">
                {SECTIONS.map((item, i) => (
                    <DashboardItem key={i} details={item} />
                ))}
            </Row>
        </div>
    );
};

export default AccountDashboard;
